import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Snackbar,
    Divider,
    TablePagination,
} from '@mui/material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { authService } from '../services/authService';
import { appointmentService } from '../services/appointmentService';
import { spreadsService } from '../services/spreadsService';
import { Appointment } from '../types/appointment';
import { Spread } from '../types/spread';
import { useNavigate, useLocation } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { parse, isValid } from 'date-fns';
import { Info as InfoIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { errorHandler } from '../utils/errorHandler';

interface PointsHistoryItem {
    id: string;
    points: number;
    reason: string;
    createdAt: string | null;
}

interface PointsHistoryState {
    items: PointsHistoryItem[];
    totalCount: number;
    lastDoc: any;
    loading: boolean;
    error: string | null;
}

interface AppointmentWithSpread extends Appointment {
    spreadName: string;
}

interface UserProfile {
    name: string;
    email: string;
    phone: string;
    points: number;
    isActive: boolean;
    createdAt: Date;
    updatedAt?: Date;
}

interface PaginationState {
    page: number;
    pageSize: number;
}

export const Profile: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [userAppointments, setUserAppointments] = useState<AppointmentWithSpread[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [spreads, setSpreads] = useState<Spread[]>([]);
    const [loadingSpreads, setLoadingSpreads] = useState(true);
    const [spreadsError, setSpreadsError] = useState<string | null>(null);
    const [pointsHistory, setPointsHistory] = useState<PointsHistoryState>({
        items: [],
        totalCount: 0,
        lastDoc: null,
        loading: true,
        error: null
    });
    const [loadingPoints, setLoadingPoints] = useState(true);
    const [pointsError, setPointsError] = useState<string | null>(null);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [profileError, setProfileError] = useState<string | null>(null);
    
    // Profil düzenleme state'leri
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        displayName: '',
        phone: '',
        currentPassword: ''
    });
    const [editError, setEditError] = useState<string | null>(null);
    const [editSuccess, setEditSuccess] = useState(false);

    // Şifre değiştirme state'leri
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwordFormData, setPasswordFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    // Snackbar state
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    const [appointmentsPagination, setAppointmentsPagination] = useState<PaginationState>({
        page: 0,
        pageSize: 5
    });

    const [pointsPagination, setPointsPagination] = useState<PaginationState>({
        page: 0,
        pageSize: 5
    });

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const loadPointsHistory = async (newPage?: number) => {
        try {
            const currentUser = authService.getCurrentUser();
            if (!currentUser) {
                throw new Error('Kullanıcı bulunamadı');
            }

            setPointsHistory(prev => ({ ...prev, loading: true, error: null }));
            
            const pageSize = pointsPagination.pageSize;
            const page = newPage !== undefined ? newPage : pointsPagination.page;
            
            const response = await authService.getPointsHistory(
                currentUser.uid,
                pageSize,
                page > 0 ? pointsHistory.lastDoc : null
            );

            setPointsHistory({
                items: response.items,
                totalCount: response.totalCount,
                lastDoc: response.lastDoc,
                loading: false,
                error: null
            });
        } catch (error) {
            setPointsHistory(prev => ({
                ...prev,
                loading: false,
                error: error instanceof Error ? error.message : 'Puan geçmişi yüklenirken bir hata oluştu'
            }));
        }
    };

    const loadUserData = async () => {
        try {
            setLoading(true);
            setError(null);

            const currentUser = authService.getCurrentUser();
            if (!currentUser) {
                throw new Error('Kullanıcı bulunamadı');
            }

            // Load spreads first
            const spreadsData = await spreadsService.getAll();
            setSpreads(spreadsData);
            setLoadingSpreads(false);

            // Then load appointments and combine with spread data
            const appointments = await appointmentService.getByUserId(currentUser.uid);
            const appointmentsWithSpreads = appointments.map(appointment => {
                const spread = spreadsData.find(s => s.id === appointment.spreadId);
                return {
                    ...appointment,
                    spreadName: spread?.title || 'Bilinmeyen Fal'
                };
            }).sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());

            setUserAppointments(appointmentsWithSpreads);

            // Load points history
            await loadPointsHistory();

            // Load user profile
            const profile = await authService.getUserProfile(currentUser.uid);
            setUserProfile(profile);
            setLoadingProfile(false);
        } catch (error) {
            if (error instanceof Error) {
                setError(error.message);
            } else {
                setError('Veriler yüklenirken bir hata oluştu');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        setEditFormData({
            displayName: userProfile?.name || '',
            phone: userProfile?.phone || '',
            currentPassword: ''
        });
        setEditError(null);
        setEditSuccess(false);
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setEditError(null);
        setEditSuccess(false);
    };

    const handleEditSubmit = async () => {
        try {
            setEditError(null);
            const currentUser = authService.getCurrentUser();
            if (!currentUser) {
                setEditError('Oturum açık değil');
                return;
            }

            if (!editFormData.currentPassword) {
                setEditError('Mevcut şifrenizi girmelisiniz');
                return;
            }

            await authService.updateUserProfile(
                currentUser.uid,
                editFormData.currentPassword,
                {
                    name: editFormData.displayName,
                    phone: editFormData.phone
                }
            );

            setEditSuccess(true);
            loadUserData(); // Profil bilgilerini yenile
            setTimeout(() => {
                setEditDialogOpen(false);
                setEditSuccess(false);
            }, 1500);
        } catch (err: any) {
            if (err.message === 'wrong-password') {
                setEditError('Mevcut şifreniz yanlış');
            } else {
                setEditError('Profil güncellenirken bir hata oluştu');
            }
        }
    };

    const handlePasswordClick = () => {
        setPasswordFormData({
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        });
        setPasswordError(null);
        setPasswordSuccess(false);
        setPasswordDialogOpen(true);
    };

    const handlePasswordClose = () => {
        setPasswordDialogOpen(false);
        setPasswordError(null);
        setPasswordSuccess(false);
    };

    const handlePasswordSubmit = async () => {
        try {
            setPasswordError(null);
            
            // Şifre validasyonları
            if (!passwordFormData.currentPassword) {
                setPasswordError('Mevcut şifrenizi girmelisiniz');
                return;
            }
            if (!passwordFormData.newPassword) {
                setPasswordError('Yeni şifrenizi girmelisiniz');
                return;
            }
            if (passwordFormData.newPassword.length < 6) {
                setPasswordError('Yeni şifre en az 6 karakter olmalıdır');
                return;
            }
            if (passwordFormData.newPassword !== passwordFormData.confirmNewPassword) {
                setPasswordError('Yeni şifreler eşleşmiyor');
                return;
            }

            await authService.changePassword(
                passwordFormData.currentPassword,
                passwordFormData.newPassword
            );

            setPasswordSuccess(true);
            setTimeout(() => {
                setPasswordDialogOpen(false);
                setPasswordSuccess(false);
            }, 1500);
        } catch (err: any) {
            if (err.message === 'wrong-password') {
                setPasswordError('Mevcut şifreniz yanlış');
            } else {
                setPasswordError('Şifre değiştirilirken bir hata oluştu');
            }
        }
    };

    const getStatusChip = (status: string, paymentStatus?: string) => {
        let label = '';
        let color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' = 'default';

        switch (status) {
            case 'pending':
                label = 'Beklemede';
                color = 'warning';
                break;
            case 'confirmed':
                label = 'Onaylandı';
                color = 'success';
                break;
            case 'completed':
                label = 'Tamamlandı';
                color = 'info';
                break;
            case 'rejected':
                label = 'Reddedildi';
                color = 'error';
                break;
            default:
                label = status;
                color = 'default';
        }

        // If status is pending, show payment status
        if (status === 'pending' && paymentStatus) {
            switch (paymentStatus) {
                case 'waiting_confirmation':
                    label = 'Ödeme Onayı Bekleniyor';
                    color = 'warning';
                    break;
                case 'confirmed':
                    label = 'Onay Bekleniyor';
                    color = 'warning';
                    break;
                default:
                    // Keep the default pending status
                    break;
            }
        }

        return (
            <Chip
                label={label}
                color={color}
                size="small"
            />
        );
    };

    const isValidDate = (date: any): boolean => {
        if (!date) return false;
        const d = new Date(date);
        return d instanceof Date && !isNaN(d.getTime());
    };

    const parseTimestamp = (timestamp: any): Date | null => {
        try {
            if (!timestamp) return null;
            if (timestamp instanceof Date) return timestamp;
            if (timestamp.toDate) return timestamp.toDate();
            return new Date(timestamp);
        } catch (error) {
            errorHandler.handleError(error, 'Error parsing timestamp');
            return null;
        }
    };

    const formatDate = (dateStr: string | null): string => {
        try {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return format(date, 'd MMMM yyyy', { locale: tr });
        } catch (error) {
            errorHandler.handleError(error, 'Error formatting date');
            return '-';
        }
    };

    const formatTime = (time: string | null): string => {
        try {
            if (!time) return '-';
            const [hours, minutes] = time.split(':');
            return `${hours.padStart(2, '0')}:${minutes || '00'}`;
        } catch (error) {
            errorHandler.handleError(error, 'Error formatting time');
            return '-';
        }
    };

    // Sayfalanmış randevuları al
    const paginatedAppointments = userAppointments.slice(
        appointmentsPagination.page * appointmentsPagination.pageSize,
        (appointmentsPagination.page + 1) * appointmentsPagination.pageSize
    );

    // Sayfalanmış puan geçmişini al
    const paginatedPointsHistory = pointsHistory.items.slice(
        pointsPagination.page * pointsPagination.pageSize,
        (pointsPagination.page + 1) * pointsPagination.pageSize
    );

    // Sayfalama işleyicileri
    const handleAppointmentPageChange = (event: unknown, newPage: number) => {
        setAppointmentsPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleAppointmentRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAppointmentsPagination({
            page: 0,
            pageSize: parseInt(event.target.value, 10)
        });
    };

    const handlePointsPageChange = async (event: unknown, newPage: number) => {
        setPointsPagination(prev => ({ ...prev, page: newPage }));
        await loadPointsHistory(newPage);
    };

    const handlePointsRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPointsPagination({
            page: 0,
            pageSize: newPageSize
        });
        await loadPointsHistory(0);
    };

    useEffect(() => {
        const init = async () => {
            const currentUser = authService.getCurrentUser();
            if (!currentUser) {
                navigate('/login');
                return;
            }
            await loadUserData();
        };

        init();

        // Check for success message in navigation state
        const state = location.state as { showSuccessMessage?: boolean; message?: string };
        if (state?.showSuccessMessage && state?.message) {
            showSnackbar(state.message, 'success');
            // Clear the state
            window.history.replaceState({}, document.title);
        }
    }, [navigate, location]);

    // Yükleme durumunda loading göster
    if (loading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    // Kritik hata durumunda hata mesajı göster
    if (error) {
        return (
            <Container>
                <Box sx={{ mt: 4 }}>
                    <Alert severity="error">
                        {error}
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => {
                                setError(null);
                                loadUserData();
                            }}
                            sx={{ ml: 2 }}
                        >
                            Tekrar Dene
                        </Button>
                    </Alert>
                </Box>
            </Container>
        );
    }

    // Kullanıcı profili yoksa hata göster
    if (!userProfile) {
        return (
            <Container>
                <Box sx={{ mt: 4 }}>
                    <Alert severity="warning">
                        Profil bilgileri yüklenemedi. Lütfen tekrar giriş yapın.
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => navigate('/login')}
                            sx={{ ml: 2 }}
                        >
                            Giriş Yap
                        </Button>
                    </Alert>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Profil Bilgileri */}
                    <Paper sx={{ p: 4, mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Typography variant="h4" gutterBottom>
                                Profilim
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={handleEditClick}
                                sx={{ borderRadius: '20px' }}
                            >
                                Düzenle
                            </Button>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Ad Soyad
                                </Typography>
                                <Typography variant="body1">
                                    {userProfile.name || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    E-posta
                                </Typography>
                                <Typography variant="body1">
                                    {userProfile.email}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Telefon
                                </Typography>
                                <Typography variant="body1">
                                    {userProfile.phone || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Puanlarım
                                    </Typography>
                                    <Tooltip title="Puanlarınızı randevu alırken kullanabilirsiniz">
                                        <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                </Box>
                                <Typography variant="body1">
                                    {userProfile.points || 0} Puan
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="text"
                                onClick={handlePasswordClick}
                                sx={{ textTransform: 'none' }}
                            >
                                Şifre Değiştir
                            </Button>
                        </Box>
                    </Paper>

                    {/* Randevular */}
                    <Paper sx={{ p: 4, mb: 4 }}>
                        <Typography variant="h5" gutterBottom>
                            Son 10 Randevum
                        </Typography>
                        {userAppointments.length > 0 ? (
                            <>
                                <List>
                                    {paginatedAppointments.map((appointment, index) => (
                                        <React.Fragment key={appointment.id}>
                                            <ListItem alignItems="flex-start">
                                                <ListItemText
                                                    primary={
                                                        <Typography component="div" variant="subtitle1">
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span>{appointment.spreadName}</span>
                                                                {getStatusChip(appointment.status, appointment.paymentStatus)}
                                                            </Box>
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography component="div" variant="body2" color="text.secondary">
                                                            {format(new Date(`${appointment.date} ${appointment.time}`), 'dd MMMM yyyy HH:mm', { locale: tr })}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                            {index < paginatedAppointments.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                                <TablePagination
                                    component="div"
                                    count={userAppointments.length}
                                    page={appointmentsPagination.page}
                                    onPageChange={handleAppointmentPageChange}
                                    rowsPerPage={appointmentsPagination.pageSize}
                                    onRowsPerPageChange={handleAppointmentRowsPerPageChange}
                                    labelRowsPerPage="Sayfa başına randevu"
                                    labelDisplayedRows={({ from, to, count }) => 
                                        `${from}-${to} / ${count !== -1 ? count : `${to} üzeri`}`
                                    }
                                    rowsPerPageOptions={[5, 10]}
                                />
                            </>
                        ) : (
                            <Typography variant="body1" color="text.secondary">
                                Henüz randevunuz bulunmuyor.
                            </Typography>
                        )}
                    </Paper>

                    {/* Puan Geçmişi */}
                    <Paper sx={{ p: 4 }}>
                        <Typography variant="h5" gutterBottom>
                            Puan Geçmişi
                        </Typography>
                        {pointsHistory.loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : pointsHistory.error ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {pointsHistory.error}
                            </Alert>
                        ) : pointsHistory.items.length > 0 ? (
                            <>
                                <List>
                                    {pointsHistory.items.map((item, index) => (
                                        <React.Fragment key={item.id}>
                                            <ListItem alignItems="flex-start">
                                                <ListItemText
                                                    primary={
                                                        <Typography component="div" variant="subtitle1">
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span>{item.reason}</span>
                                                                <span style={{
                                                                    color: item.points > 0 ? theme.palette.success.main : theme.palette.error.main
                                                                }}>
                                                                    {item.points > 0 ? '+' : ''}{item.points} Puan
                                                                </span>
                                                            </Box>
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography component="div" variant="body2" color="text.secondary">
                                                            {item.createdAt ? formatDate(item.createdAt) : '-'}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                            {index < pointsHistory.items.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                                <TablePagination
                                    component="div"
                                    count={pointsHistory.totalCount}
                                    page={pointsPagination.page}
                                    onPageChange={handlePointsPageChange}
                                    rowsPerPage={pointsPagination.pageSize}
                                    onRowsPerPageChange={handlePointsRowsPerPageChange}
                                    labelRowsPerPage="Sayfa başına işlem"
                                    labelDisplayedRows={({ from, to, count }) => 
                                        `${from}-${to} / ${count !== -1 ? count : `${to} üzeri`}`
                                    }
                                    rowsPerPageOptions={[5, 10, 25]}
                                />
                            </>
                        ) : (
                            <Typography variant="body1" color="text.secondary">
                                Henüz puan işlemi bulunmuyor.
                            </Typography>
                        )}
                    </Paper>
                </motion.div>
            </Box>

            {/* Profil Düzenleme Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Profil Düzenle
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {editError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {editError}
                            </Alert>
                        )}
                        {editSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Profil başarıyla güncellendi
                            </Alert>
                        )}
                        <TextField
                            fullWidth
                            label="Ad Soyad"
                            value={editFormData.displayName}
                            onChange={(e) => setEditFormData({ ...editFormData, displayName: e.target.value })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Telefon"
                            value={editFormData.phone}
                            onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Mevcut Şifre"
                            type="password"
                            value={editFormData.currentPassword}
                            onChange={(e) => setEditFormData({ ...editFormData, currentPassword: e.target.value })}
                            margin="normal"
                            required
                            helperText="Değişiklikleri onaylamak için mevcut şifrenizi girin"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>İptal</Button>
                    <Button onClick={handleEditSubmit} variant="contained" disabled={!editFormData.currentPassword}>
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Şifre Değiştirme Dialog */}
            <Dialog open={passwordDialogOpen} onClose={handlePasswordClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Şifre Değiştir
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {passwordError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {passwordError}
                            </Alert>
                        )}
                        {passwordSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Şifreniz başarıyla değiştirildi
                            </Alert>
                        )}
                        <TextField
                            fullWidth
                            label="Mevcut Şifre"
                            type="password"
                            value={passwordFormData.currentPassword}
                            onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Yeni Şifre"
                            type="password"
                            value={passwordFormData.newPassword}
                            onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Yeni Şifre (Tekrar)"
                            type="password"
                            value={passwordFormData.confirmNewPassword}
                            onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmNewPassword: e.target.value })}
                            margin="normal"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePasswordClose}>İptal</Button>
                    <Button 
                        onClick={handlePasswordSubmit} 
                        variant="contained"
                        disabled={!passwordFormData.currentPassword || !passwordFormData.newPassword || !passwordFormData.confirmNewPassword}
                    >
                        Şifreyi Değiştir
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}; 